import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, NavLink, useNavigate } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import queryString from 'query-string';
import {
  Table,
  useQueryState,
  Filters,
  useFilters,
  IconExpand,
  ButtonPrimary,
  ButtonSize,
  usePrevious,
  BaseTransaction,
  useGetList,
  PermissionEnum,
  SchemaFilterType,
  BaseellaStatus,
  ButtonSecondary,
  SignaturePopup,
  ButtonPseudo,
  IconDownload,
  SelectItemProps,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useBatchCreateTransactionSignatureMutation,
  useSendCodeMutation,
  useValidateCodeMutation,
} from '../../store/signature/service';
import { MfaToken, MfaType } from '../../store/types';
import {
  clearNostroListState,
  setExpandedTransactionId,
  setSelectedCount,
} from './store/slices/list';
import { setNotificationVisible } from '../../../../../store/slice';
import {
  columns,
  columnsLabels,
  expandableColumns,
  filtersSchema,
  schema,
} from './variables/list';
import {
  useLazyGetNostroListQuery,
  useLazyGetNostroListStatusesQuery,
  useExportNostroTransactionMutation,
} from './store/services';
import { useLazyGetGeneralLedgerTransactionValuesQuery } from '../transactions/store/services';
import formatNostroList from './helpers/format-nostro-list';

import ROUTES from '../../routes';

import cn from './styles.module.css';
import { clearMfaToken } from '../../store/signature/slice';

let timeout: NodeJS.Timeout;
let timer: NodeJS.Timeout;

const NostroList = (): React.JSX.Element => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    root: { userId },
    nostro: {
      list: {
        pagination,
        transactions,
        expandedTransactionId,
        collapseAllRows,
        selectedCount,
      },
    },
    transactionsDictionaries: {
      currencies,
      activePermissions,
      paymentTypes,
      pspPartnerAccounts,
      currenciesList,
    },
    signatures: { mfaToken },
  } = useAppSelector((state) => state);

  const [sendCode, { isLoading: sendCodeInProgress }] = useSendCodeMutation();
  const [validateCode, { isLoading: validateCodeInProgress }] =
    useValidateCodeMutation();
  const [
    createSignature,
    {
      isLoading: createSignatureInProgress,
      isSuccess: createSignatureIsSuccess,
    },
  ] = useBatchCreateTransactionSignatureMutation();

  const [unsignedTransactionsList, setUnsignedTransactionsList] = useState<
    string[]
  >([]);

  const [signaturePopupVisible, setSignaturePopupVisible] = useState(false);
  const [errorMessageSignature, setErrorMessageSignature] = useState('');
  const [disabledRowIds, setDisabledRowIds] = useState<string[]>([]);

  const [includeRemoved, setIncludeRemoved] = useState(false);
  const [onlyRemoved, setOnlyRemoved] = useState(false);
  const [savedFilters, setSavedFilters] = useState<
    {
      id: string;
      name: string;
      value: Record<string, any>;
      default?: boolean;
      employeeId: string;
    }[]
  >([]);
  const [selectedSavedFilter, setSelectedSavedFilter] = useState('');

  const prevTransactions = usePrevious(transactions);

  const navigate = useNavigate();
  const { search } = useLocation();
  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);
  const prevFilters = usePrevious(filters);
  const dispatch = useAppDispatch();

  const [
    exportTransaction,
    { isSuccess: exportIsSuccess, isLoading: exportIsLoading },
  ] = useExportNostroTransactionMutation();

  const onExportClick = async () => {
    const exportFilters: Record<string, string[] | string | number> = {};

    if (filters && Object.keys(filters)?.length) {
      Object.keys(filters)?.forEach((item) => {
        if (item !== 'cursor') {
          if (item !== 'transferDate') {
            if (
              item === 'consumerStatus' ||
              item === 'baseellaStatus' ||
              item === 'monitoringStatus' ||
              item === 'benificiaryType' ||
              item === 'chargeType' ||
              item === 'id' ||
              item === 'senderUserId' ||
              item === 'recipientUserId' ||
              item === 'currencyId' ||
              item === 'paymentOrderId' ||
              item === 'typeId' ||
              item === 'excludeAccounts'
            ) {
              exportFilters[item] = filters[item].value;
            } else {
              const [filterValue] = filters[item].value;

              exportFilters[item] = filterValue;
            }
          } else if (item === 'transferDate') {
            const transferDate = filters[item].value;

            const [from, to] = transferDate;

            exportFilters.transferDateFrom = from;

            if (to) {
              exportFilters.transferDateTo = to;
            }
          }
        }
      });
    }

    await exportTransaction(exportFilters);
  };

  const [getTransactionsList, { isLoading, isFetching: nostroListFetching }] =
    useLazyGetNostroListQuery();
  const [getNostroStatuses] = useLazyGetNostroListStatusesQuery();

  const [
    getGeneralLedgerTransactionValues,
    { isLoading: glIsLoading, isFetching, isSuccess },
  ] = useLazyGetGeneralLedgerTransactionValuesQuery();

  const getGeneralLedgerAction = (id: string) => {
    dispatch(setExpandedTransactionId(id));
    getGeneralLedgerTransactionValues(id);
  };

  const { getListAction, setPage, onFilter, onSort, errorList } =
    useGetList<BaseTransaction>({
      getList: getTransactionsList,
      onFilterChange,
      onSortChange,
      schema,
    });

  const onSendCode = async () => {
    clearTimeout(timer);

    const response = (await sendCode({ mfa: MfaType.TOTP })) as {
      data: { data: MfaToken };
    };

    if (response && response.data && response.data.data) {
      setSignaturePopupVisible(true);

      const expireTime = Math.round(
        // eslint-disable-next-line react-hooks/purity
        new Date(response.data.data.expiresAt).getTime() - Date.now(),
      );

      timer = setTimeout(() => {
        onSendCode();
      }, expireTime);
    }
  };

  const validateCodeAction = async (code: string) => {
    clearTimeout(timer);
    setErrorMessageSignature('');

    if (code) {
      const response = (await validateCode({
        mfaToken: mfaToken as string,
        totpToken: code,
      })) as {
        data?: { data: MfaToken };
        error: { data: { message: string; statusCode: number } };
      };

      if (response && response.data && response.data.data) {
        const createResponse = (await createSignature({
          mfaToken: response.data.data.token,
          transactionId: unsignedTransactionsList,
        })) as {
          error: { data: { message: string }; status: number };
        };

        if (
          createResponse &&
          createResponse.error &&
          createResponse.error.data
        ) {
          if (createResponse.error?.status === 403) {
            setErrorMessageSignature('Access denied');
          } else {
            // eslint-disable-next-line no-lonely-if
            if (!Array.isArray(createResponse.error.data.message)) {
              setErrorMessageSignature(createResponse.error.data.message);
            } else if (Array.isArray(createResponse.error.data.message)) {
              setErrorMessageSignature(
                'Internal error. Transaction wasn`t signed',
              );
            }
          }
        }
      } else if (response && response.error && response.error.data) {
        if (response.error.data.statusCode === 403) {
          setErrorMessageSignature('Access denied');
        } else if (response.error.data.statusCode === 401) {
          setErrorMessageSignature('Incorrect OTP code');
        } else {
          setErrorMessageSignature(response.error.data.message);
        }
      }
    } else {
      setErrorMessageSignature('OTP Code should not be empty');
    }
  };

  const onSavedFilterClick = (id: string, savedFilter: Filters) => {
    const filterValues: Filters = {
      ...savedFilter,
      take: {
        value: [(selectedCount as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    onFilter(filterValues);
    setSelectedSavedFilter(id);
  };

  const onClearTimeout = async () => {
    await clearTimeout(timeout);
  };

  const onGetStatuses = async (query: string) => {
    await onClearTimeout();

    timeout = setTimeout(async () => {
      await getNostroStatuses(query);
      await onGetStatuses(query);
    }, 5000);
  };

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const handleClose = () => {
    setSignaturePopupVisible(false);
    setErrorMessageSignature('');
    dispatch(clearMfaToken());
  };

  const onDuplicate = (id: string) => {
    const currentTransaction = transactions?.find((item) => item.id === id);

    if (currentTransaction) {
      navigate(`${ROUTES.NOSTRO_TRANSFER_CREATE}${search || ''}`, {
        state: { ...currentTransaction, duplicate: true },
      });
    }
  };

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 58;

        dispatch(setSelectedCount(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    setValues('currencyId', currencies as SelectItemProps[]);
    setValues(
      'paymentType',
      paymentTypes?.map((item) => ({
        name: item.name,
        value: item.paymentType,
      })) || [],
    );
    setValues('senderAccountNumber', pspPartnerAccounts as SelectItemProps[]);
    setValues(
      'recipientAccountNumber',
      pspPartnerAccounts as SelectItemProps[],
    );
  }, [currencies, paymentTypes, pspPartnerAccounts]);

  useEffect(() => {
    if (exportIsSuccess) {
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'nostro',
        }),
      );
    }
  }, [exportIsSuccess]);

  useEffect(() => {
    if (
      selectedCount &&
      selectedCount !== 0 &&
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !transactions &&
      sort &&
      sort.sortProp
    ) {
      let parsed;

      if (search) {
        parsed = queryString.parse(search);
      }

      let filterValues: Filters = {
        take: {
          value: [selectedCount?.toString()],
          type: SchemaFilterType.STRING,
        },
      };

      const savedFiltersJSON = localStorage.getItem('nostro-filters');

      let parsedSavedFilters: {
        id: string;
        name: string;
        value: Record<string, any>;
        default?: boolean;
      }[];

      let defaultSavedFilter;

      if (!Object.keys(filters)?.length) {
        if (savedFiltersJSON) {
          parsedSavedFilters = JSON.parse(savedFiltersJSON);

          if (parsedSavedFilters && parsedSavedFilters?.length) {
            const currentUserParsedFilters = parsedSavedFilters?.filter(
              (item: any) => item.employeeId === userId,
            );

            if (currentUserParsedFilters && currentUserParsedFilters?.length) {
              defaultSavedFilter = currentUserParsedFilters?.find(
                (item) => item.default,
              );
            }
          }
        }

        if (defaultSavedFilter) {
          setSelectedSavedFilter(defaultSavedFilter.id);

          filterValues = {
            ...filterValues,
            ...defaultSavedFilter?.value,
          };
        } else {
          filterValues = {
            ...filterValues,
            ...filters,
          };
        }
      } else {
        filterValues = {
          ...filterValues,
          ...filters,
        };
      }

      if (parsed && parsed.includeRemoved) {
        setIncludeRemoved(true);

        filterValues.includeRemoved = {
          value: ['true'],
          type: SchemaFilterType.STRING,
        };
      }

      if (parsed && parsed.onlyRemoved) {
        setOnlyRemoved(true);

        filterValues.onlyRemoved = {
          value: ['true'],
          type: SchemaFilterType.STRING,
        };
      }

      getListAction({
        filters: filterValues,
        sort,
      });
    }
  }, [filters, sort, prevFilters, transactions, selectedCount, userId]);

  useEffect(() => {
    if (createSignatureIsSuccess) {
      const nextDisabledRowIds = ([] as string[]).concat(disabledRowIds);

      unsignedTransactionsList.forEach((item) => {
        nextDisabledRowIds.push(item);
      });

      setDisabledRowIds(nextDisabledRowIds);

      handleClose();

      if (transactions && transactions.length) {
        const ids = transactions.map((item) => item.id);
        const query = `?id=${ids.join('&id=')}`;

        setTimeout(() => {
          onGetStatuses(query);
        }, 300);
      }
    }
  }, [createSignatureIsSuccess, transactions]);

  useEffect(() => {
    if (transactions) {
      setDisabledRowIds(
        transactions
          ?.filter((item) => item.baseellaStatus !== BaseellaStatus.NOT_SIGNED)
          ?.map((item) => item.id),
      );
    }
  }, [transactions]);

  useEffect(
    () => () => {
      setTimeout(async () => {
        await onClearTimeout();
      });

      dispatch(clearNostroListState());
    },
    [dispatch],
  );

  const formattedData = useMemo(() => {
    const data = formatNostroList({
      list: transactions as BaseTransaction[],
      currenciesList,
      paymentTypes,
    });

    return data;
  }, [transactions, currenciesList, paymentTypes]);

  useEffect(() => {
    if (!isEqual(prevTransactions, transactions) && expandedTransactionId) {
      getGeneralLedgerAction(expandedTransactionId);
    }
  }, [prevTransactions, transactions, expandedTransactionId]);

  useEffect(() => {
    if (transactions && transactions.length) {
      const ids = transactions.map((item) => item.id);
      const query = `?id=${ids.join('&id=')}`;

      onGetStatuses(query);
    }
  }, [transactions]);

  useEffect(() => {
    const savedFiltersJSON = localStorage.getItem('nostro-filters');

    if (savedFiltersJSON) {
      const parsed = JSON.parse(savedFiltersJSON);

      if (parsed) {
        const currentEmployeeSavedFilters = parsed?.filter(
          (item: any) => item.employeeId === userId,
        );

        setSavedFilters(currentEmployeeSavedFilters);
      }
    }
  }, [userId]);

  const content: React.JSX.Element = (
    <Table
      title="Nostro transfers"
      columns={columns}
      data={formattedData || []}
      onSort={(name) => {
        onSort(name);
        onClearTimeout();
      }}
      filters={filters}
      sort={sort}
      filtersComponent={filtersComponent}
      columnsLabels={columnsLabels}
      onFilterChange={(values) => {
        setSelectedSavedFilter('');

        const filterValues: Filters = {
          ...values,
          take: {
            value: [(selectedCount as number)?.toString()],
            type: SchemaFilterType.STRING,
          },
        };

        if (includeRemoved) {
          filterValues.includeRemoved = {
            value: ['true'],
            type: SchemaFilterType.STRING,
          };
        }

        if (onlyRemoved) {
          filterValues.onlyRemoved = {
            value: ['true'],
            type: SchemaFilterType.STRING,
          };
        }

        onFilter(filterValues);
        onClearTimeout();
      }}
      handleSubmit={handleSubmit}
      isLoading={
        isLoading ||
        nostroListFetching ||
        !selectedCount ||
        transactions === undefined
      }
      take={(selectedCount as number)?.toString()}
      setPage={(data) => {
        const filterValues: Filters = {
          ...filters,
          take: {
            value: [(selectedCount as number)?.toString()],
            type: SchemaFilterType.STRING,
          },
        };

        if (includeRemoved) {
          filterValues.includeRemoved = {
            value: ['true'],
            type: SchemaFilterType.STRING,
          };
        }

        if (onlyRemoved) {
          filterValues.onlyRemoved = {
            value: ['true'],
            type: SchemaFilterType.STRING,
          };
        }

        setPage({ ...data, filters: filterValues });
        onClearTimeout();
      }}
      pagination={pagination}
      hiddedAllFilterTags={Boolean(selectedSavedFilter)}
      topContent={
        <>
          <div className={cn.exportRow}>
            {activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_NOSTRO_CREATE_ACTION,
            ) ? (
              <div>
                <NavLink to={`${ROUTES.NOSTRO_TRANSFER_CREATE}${search || ''}`}>
                  <ButtonPrimary
                    text="New Nostro transfer"
                    size={ButtonSize.SMALL}
                    icon={<IconExpand />}
                  />
                </NavLink>
              </div>
            ) : undefined}
            <ButtonPseudo
              text={exportIsLoading ? 'Loading...' : 'Export transactions'}
              icon={<IconDownload />}
              className={`button-white ${cn.exportButton}`}
              size={ButtonSize.SMALL}
              onClick={onExportClick}
            />
          </div>
          {unsignedTransactionsList?.length > 0 && (
            <ButtonSecondary
              className={`button-white ${cn.batchSignButton}`}
              text={
                sendCodeInProgress
                  ? 'Loading...'
                  : `Sign transactions (${unsignedTransactionsList.length})`
              }
              size={ButtonSize.SMALL}
              onClick={onSendCode}
            />
          )}
        </>
      }
      storeKey="nostro-transfers"
      expandableColumns={expandableColumns}
      error={errorList}
      onExpand={getGeneralLedgerAction}
      isLoadingExpandableRow={glIsLoading || isFetching}
      paginationDisabled={nostroListFetching}
      collapseAllRows={collapseAllRows}
      isFetching={nostroListFetching}
      onDuplicateClick={(id) => onDuplicate(id)}
      disabledRowIds={disabledRowIds}
      onRowsSelect={(ids) => setUnsignedTransactionsList(ids)}
      clearSelectedItems={createSignatureIsSuccess}
      tableClassName={!transactions?.length ? cn.nostroTable : ''}
      tableBodyClassName={cn.nostroTableBody}
      savedFilters={savedFilters}
      setSavedFilters={setSavedFilters}
      savedFiltersKey="nostro-filters"
      selectedSavedFilter={selectedSavedFilter}
      onSavedFilterClick={onSavedFilterClick}
      setSelectedSavedFilter={setSelectedSavedFilter}
      isSuccessExpandableRow={isSuccess}
      userId={userId}
      transactionTable
      withDuplicateButton
      withVerticalScroll
      expandable
      withScroll
      withSettings
      clickable
    />
  );

  return (
    <div className={cn.nostroWrapper} ref={scrollRef}>
      {content}
      <SignaturePopup
        signaturePopupVisible={signaturePopupVisible}
        validateCode={validateCodeAction}
        validateCodeInProgress={
          validateCodeInProgress || createSignatureInProgress
        }
        errorMessageSignature={errorMessageSignature}
        onSignatureCancelClick={handleClose}
      />
      <div id="signature-popup" />
    </div>
  );
};

export default NostroList;
